import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Users, Trash2, Edit2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ id: '', name: '', leader_id: '' });
  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    leader_id: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only recruiters and existing team leaders are eligible to be team leaders.
      // Admins/admin_support are rejected by the backend and filtered out here too.
      const eligible = response.data.filter(u =>
        ['recruiter', 'team_leader'].includes(u.role) && u.is_active !== false
      );
      setUsers(eligible);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const handleDeleteTeam = async (id) => {
    if (!window.confirm('Delete this team? Members will be unassigned.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/teams/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Team deleted');
      fetchTeams();
    } catch (error) { toast.error('Failed to delete team'); }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/teams/${editData.id}`, { name: editData.name, leader_id: editData.leader_id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team updated');
      setEditOpen(false);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teams`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team created successfully');
      setOpen(false);
      setFormData({ name: '', leader_id: '' });
      fetchTeams();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Teams</h1>
          <p className="text-slate-600">Manage organizational teams and leaders</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-button" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold active:scale-95 transition-transform">
              <Plus size={16} className="mr-2" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>New Team</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider">Team Name</Label>
                <Input
                  id="name"
                  data-testid="team-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2 rounded-sm"
                />
              </div>
              <div>
                <Label htmlFor="leader_id" className="text-xs font-bold uppercase tracking-wider">Team Leader</Label>
                <select
                  id="leader_id"
                  data-testid="team-leader-select"
                  value={formData.leader_id}
                  onChange={(e) => setFormData({ ...formData, leader_id: e.target.value })}
                  required
                  className="mt-2 flex h-10 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                >
                  <option value="">{users.length === 0 ? 'No eligible users — add a recruiter first' : 'Select a team leader'}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email}) — {user.role.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={submitting} data-testid="submit-team-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? 'Creating...' : 'Create Team'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Edit Team</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateTeam} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Team Name</Label>
                <Input data-testid="edit-team-name-input" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} required className="mt-2 rounded" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Team Leader</Label>
                <select data-testid="edit-team-leader-select" value={editData.leader_id} onChange={(e) => setEditData({ ...editData, leader_id: e.target.value })} required className="mt-2 w-full h-10 border border-slate-300 rounded px-3 text-sm">
                  <option value="">Select a leader…</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email}) — {u.role.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" data-testid="submit-edit-team-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold">Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200 bg-slate-50">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="teams-search-input"
              placeholder="Search teams by name or leader…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 rounded"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold text-slate-900">Team Name</TableHead>
              <TableHead className="font-bold text-slate-900">Team Leader</TableHead>
              <TableHead className="font-bold text-slate-900">Created On</TableHead>
              {isAdmin && <TableHead className="font-bold text-slate-900">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const q = searchText.trim().toLowerCase();
              const filtered = q
                ? teams.filter(t => (t.name || '').toLowerCase().includes(q) || (t.leader_name || '').toLowerCase().includes(q))
                : teams;
              return filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                  <Users size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>{q ? 'No teams match your search.' : 'No teams yet. Create your first team!'}</p>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((team) => (
                <TableRow key={team.id} data-testid={`team-row-${team.id}`}>
                  <TableCell className="font-semibold">{team.name}</TableCell>
                  <TableCell>{team.leader_name}</TableCell>
                  <TableCell className="text-slate-500">{new Date(team.created_at).toLocaleDateString()}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" data-testid={`edit-team-${team.id}`} title="Edit team"
                          onClick={() => { setEditData({ id: team.id, name: team.name, leader_id: team.leader_id || '' }); setEditOpen(true); }}
                          className="text-blue-600 hover:text-blue-900 hover:bg-blue-50">
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-team-${team.id}`} onClick={() => handleDeleteTeam(team.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            );
            })()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
